import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SnackBar from "./SnackBar";
import { upgrade_plan, verify_payment, get_user_plan, get_admin_plans } from "../Api service/APIvariables";

export default function Subscription() {
  const [currentPlan, setCurrentPlan] = useState(1);
  const [dbPlans, setDbPlans] = useState([]);
  const [planDetails, setPlanDetails] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [isProcessing, setIsProcessing] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(false);
  const [color, setColor] = useState(false);
  const [message, setMessage] = useState("");

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchCurrentPlan = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      const res = await fetch(get_user_plan, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error && data.plan_id) {
        setCurrentPlan(parseInt(data.plan_id));
        setPlanDetails(data);
        if (data.billing_cycle && data.billing_cycle !== "free") {
          setBillingCycle(data.billing_cycle);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAdminPlans = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      const res = await fetch(get_admin_plans, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setDbPlans(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCurrentPlan();
    fetchAdminPlans();
  }, []);

  const handleUpgrade = async (plan_id, plan_name) => {
    setIsProcessing(true);
    try {
      const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!res) {
        setMessage("Razorpay SDK failed to load");
        setStatus(false); setColor(false); setOpen(true);
        setIsProcessing(false);
        return;
      }

      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      const user_name = localStorage.getItem("user_name") || "";
      const user_email = localStorage.getItem("user_email") || "";

      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("plan_id", plan_id);
      formData.append("billing_cycle", billingCycle);

      const orderRes = await fetch(upgrade_plan, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const orderData = await orderRes.json();

      if (orderData.error) {
        setMessage(orderData.message || "Failed to initiate payment");
        setStatus(false); setColor(false); setOpen(true);
        setIsProcessing(false);
        return;
      }

      const options = {
        key: orderData.data.key_id || "rzp_test_YourTestKeyIdHere",
        amount: orderData.data.amount,
        currency: orderData.data.currency,
        name: "Quickcard",
        description: `Upgrade to ${plan_name} Plan (${billingCycle})`,
        order_id: orderData.data.id,
        handler: async function (response) {
          const verifyData = new FormData();
          verifyData.append("user_id", user_id);
          verifyData.append("plan_id", plan_id);
          verifyData.append("billing_cycle", billingCycle);
          verifyData.append("razorpay_payment_id", response.razorpay_payment_id);
          verifyData.append("razorpay_order_id", response.razorpay_order_id);
          verifyData.append("razorpay_signature", response.razorpay_signature);

          const verifyRes = await fetch(verify_payment, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: verifyData,
          });
          const verifyJson = await verifyRes.json();

          if (verifyJson.error) {
            setMessage(verifyJson.message);
            setStatus(false); setColor(false); setOpen(true);
          } else {
            setMessage("Successfully upgraded plan!");
            setStatus(true); setColor(true); setOpen(true);
            fetchCurrentPlan(); // refresh
            window.dispatchEvent(new Event("planUpdated"));
          }
        },
        prefill: { name: user_name, email: user_email },
        theme: { color: "#453c72" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        setMessage("Payment failed: " + response.error.description);
        setStatus(false); setColor(false); setOpen(true);
      });
      rzp.open();
    } catch (err) {
      setMessage("An error occurred during payment initialization");
      setStatus(false); setColor(false); setOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const getDynamicPrice = (planId, fallbackPrice, fallbackYearlyPrice) => {
    const dbPlan = dbPlans.find((p) => parseInt(p.id) === planId);
    if (!dbPlan) {
      return billingCycle === "yearly" ? fallbackYearlyPrice : fallbackPrice;
    }
    const priceVal = billingCycle === "yearly" ? dbPlan.yearly_price : dbPlan.price;
    return `₹${parseFloat(priceVal || 0).toLocaleString()}`;
  };

  const plans = [
    {
      id: 1,
      name: "Free",
      price: "₹0",
      color: "#757575",
      features: [
        { text: "10 OCR scans/day", included: true },
        { text: "20 emails/day", included: true },
        { text: "3 templates", included: true },
        { text: "100 cards storage", included: true },
        { text: "AI Email Drafting", included: false },
        { text: "Open Tracking", included: false },
        { text: "Priority OCR Processing", included: false },
        { text: "Export Contacts CSV", included: false },
        { text: "Admin Support Priority", included: false }
      ]
    },
    {
      id: 2,
      name: "Plus",
      price: getDynamicPrice(2, "₹299", "₹2,490"),
      period: billingCycle === "yearly" ? "/yr" : "/mo",
      color: "#1976d2",
      features: [
        { text: "100 OCR scans/day", included: true },
        { text: "200 emails/day", included: true },
        { text: "10 templates", included: true },
        { text: "2,000 cards storage", included: true },
        { text: "20 AI drafts/month", included: true },
        { text: "Open Tracking", included: true },
        { text: "Export Contacts CSV", included: true },
        { text: "Priority OCR Processing", included: false },
        { text: "Admin Support Priority", included: false }
      ]
    },
    {
      id: 3,
      name: "Pro",
      tag: "Most Popular",
      price: getDynamicPrice(3, "₹599", "₹5,990"),
      period: billingCycle === "yearly" ? "/yr" : "/mo",
      color: "#9c27b0",
      features: [
        { text: "500 OCR scans/day", included: true },
        { text: "1000 emails/day", included: true },
        { text: "Unlimited templates", included: true },
        { text: "10,000 cards storage", included: true },
        { text: "500 AI drafts/month", included: true },
        { text: "Open Tracking", included: true },
        { text: "Priority OCR Processing", included: true },
        { text: "Export Contacts CSV", included: true },
        { text: "Admin Support Priority", included: true }
      ]
    },
    {
      id: 4,
      name: "Unlimited",
      price: getDynamicPrice(4, "₹999", "₹9,990"),
      period: billingCycle === "yearly" ? "/yr" : "/mo",
      color: "#fbc02d",
      features: [
        { text: "Unlimited OCR scans", included: true },
        { text: "Unlimited emails", included: true },
        { text: "Unlimited templates", included: true },
        { text: "Unlimited card storage", included: true },
        { text: "Unlimited AI drafting", included: true },
        { text: "Open Tracking", included: true },
        { text: "Priority OCR Processing", included: true },
        { text: "Export Contacts CSV", included: true },
        { text: "Admin Support Priority", included: true }
      ]
    }
  ];

  return (
    <Box sx={{ padding: 4, backgroundColor: "#f9f9f9", minHeight: "90vh" }}>
      <SnackBar open={open} message={message} setOpen={setOpen} status={status} color={color} />
      
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" fontWeight="bold" color="#453c72" gutterBottom>
          Upgrade Your Plan
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Scale your networking limits and unlock AI superpowers.
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Typography variant="body1" fontWeight={billingCycle === "monthly" ? "bold" : "normal"}>Monthly</Typography>
          <Switch 
            checked={billingCycle === "yearly"} 
            onChange={(e) => setBillingCycle(e.target.checked ? "yearly" : "monthly")} 
            color="primary"
          />
          <Typography variant="body1" fontWeight={billingCycle === "yearly" ? "bold" : "normal"}>
            Yearly <Chip label="Save up to 16%" size="small" color="success" sx={{ ml: 1, height: 20 }} />
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4} justifyContent="center" maxWidth="1200px" margin="0 auto" sx={{ pt: 4 }}>
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} md={3} key={plan.id}>
            <Card 
              elevation={currentPlan === plan.id ? 8 : 2} 
              sx={{ 
                height: "100%", 
                display: "flex", 
                flexDirection: "column",
                border: currentPlan === plan.id ? `2px solid ${plan.color}` : "none",
                position: "relative",
                overflow: "visible", // prevent cutting off absolute chips
                transition: "transform 0.2s",
                "&:hover": { transform: "translateY(-5px)" }
              }}
            >
              {plan.tag && (
                <Chip 
                  label={plan.tag}
                  size="small"
                  color="secondary"
                  sx={{ 
                    position: "absolute", 
                    top: -14, 
                    left: "50%", 
                    transform: "translateX(-50%)",
                    fontWeight: "bold",
                    zIndex: 2
                  }} 
                />
              )}
              {currentPlan === plan.id && (
                <Chip 
                  label="Current Plan" 
                  size="small"
                  sx={{ 
                    position: "absolute", 
                    top: 10, 
                    right: 10, 
                    backgroundColor: plan.color, 
                    color: "white" 
                  }} 
                />
              )}
              <CardContent sx={{ flexGrow: 1, textAlign: "center", pt: 4 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: plan.color }} gutterBottom>
                  {plan.name}
                </Typography>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {plan.price}
                  <Typography variant="span" sx={{ fontSize: "16px", fontWeight: "normal", color: "textSecondary" }}>
                    {plan.period || ""}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                  One-time payment
                </Typography>
                <List dense>
                  {plan.features.map((feature, idx) => (
                    <ListItem key={idx} disableGutters>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {feature.included ? (
                          <CheckCircleIcon sx={{ color: plan.color, fontSize: 18 }} />
                        ) : (
                          <CancelIcon sx={{ color: "#d32f2f", fontSize: 18 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature.text} 
                        primaryTypographyProps={{ 
                          fontSize: "13px",
                          fontWeight: feature.included ? "medium" : "normal",
                          color: feature.included ? "text.primary" : "text.secondary"
                        }} 
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ justifyContent: "center", pb: 3, pt: 0 }}>
                <Button 
                  variant={currentPlan === plan.id ? "outlined" : "contained"}
                  sx={{ 
                    width: "80%", 
                    backgroundColor: currentPlan === plan.id ? "transparent" : plan.color,
                    color: currentPlan === plan.id ? plan.color : "white",
                    borderColor: plan.color,
                    "&:hover": {
                      backgroundColor: currentPlan === plan.id ? "#f5f5f5" : plan.color,
                      opacity: 0.9
                    }
                  }}
                  disabled={currentPlan === plan.id || isProcessing}
                  onClick={() => handleUpgrade(plan.id, plan.name)}
                >
                  {currentPlan === plan.id 
                    ? "Active" 
                    : (plan.id < currentPlan ? "Downgrade" : "Upgrade")}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
